import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../../components/NtText";
import NtMaterialIcon from "../../../components/NtMaterialIcon";
import NtModalInputField from "../../../components/NtModalInputField";
import NtOnboardingEditorialSection from "../NtOnboardingEditorialSection";
import NtOnboardingField from "../NtOnboardingField";
import { useTheme } from "../../../context/ThemeContext";
import {
  isValidEmail,
  isValidPassword,
  MIN_PASSWORD_LENGTH,
} from "../../utils/validationUtils";

function NtOnboardingAdminStep({ admin, onChangeAdmin }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const updateAdmin = (patch) =>
    onChangeAdmin && onChangeAdmin({ ...admin, ...patch });

  // Only flag invalid when the user has typed something.
  const emailInvalid = !!admin?.email && !isValidEmail(admin.email);
  const passwordInvalid = !!admin?.password && !isValidPassword(admin.password);
  const passwordMismatch =
    !!admin?.passwordConfirm && admin.password !== admin.passwordConfirm;

  return (
    <View>
      <NtOnboardingEditorialSection
        first
        label="Admin user"
        caption="You'll sign in with this email after signup."
      >
        <View style={styles.note}>
          <NtMaterialIcon name="key-outline" size={16} color={theme.brand2} />
          <NtText style={styles.noteText}>
            <NtText style={styles.noteStrong}>Save these credentials.</NtText>{" "}
            You'll use this email and password to sign in after your account is
            created.
          </NtText>
        </View>
        <NtModalInputField
          title="Full name *"
          placeholder="Jane Doe"
          value={admin?.name || ""}
          onChangeText={(name) => updateAdmin({ name })}
        />
        <NtOnboardingField
          title="Email *"
          placeholder="jane@acme.com"
          value={admin?.email || ""}
          onChangeText={(email) => updateAdmin({ email })}
          error={emailInvalid ? "That doesn't look like a valid email address." : null}
          footer="You'll sign in with this email address."
        />
        <NtOnboardingField
          title="Password *"
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          secureTextEntry={true}
          showEye={true}
          value={admin?.password || ""}
          onChangeText={(password) => updateAdmin({ password })}
          error={
            passwordInvalid
              ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
              : null
          }
        />
        <NtOnboardingField
          title="Confirm password *"
          placeholder="Re-enter your password"
          secureTextEntry={true}
          showEye={true}
          value={admin?.passwordConfirm || ""}
          onChangeText={(passwordConfirm) => updateAdmin({ passwordConfirm })}
          error={passwordMismatch ? "Passwords don't match." : null}
        />
      </NtOnboardingEditorialSection>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    note: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      borderRadius: t.radius,
      paddingVertical: 10,
      paddingHorizontal: 12,
      marginBottom: 14,
    },
    noteText: { flex: 1, fontSize: 12, color: t.text, lineHeight: 17 },
    noteStrong: { fontWeight: "700", color: t.text },
  });

export default NtOnboardingAdminStep;
